/**
 * SizilPro — Stripe client
 *
 * Client-side: only the publishable key is used here.
 * All secret-key operations (create subscription, handle webhooks)
 * happen in the stripe-webhook Edge Function — never in the browser.
 *
 * Usage:
 *   import { openBillingPortal, getProposalTierFromModules } from "@/lib/stripe";
 */

import { loadStripe, type Stripe } from "@stripe/stripe-js";

// ─── Stripe JS singleton (lazy) ───────────────────────────────
let stripePromise: Promise<Stripe | null>;

export function getStripe(): Promise<Stripe | null> {
  const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string;
  if (!key) {
    console.warn("[stripe] VITE_STRIPE_PUBLISHABLE_KEY not set");
    return Promise.resolve(null);
  }
  if (!stripePromise) {
    stripePromise = loadStripe(key);
  }
  return stripePromise;
}

// ─── Billing portal redirect ──────────────────────────────────
/**
 * Opens the Stripe customer portal in the current tab.
 * Requires a portal session URL from the server — call the
 * create-portal-session Edge Function first.
 */
export async function openBillingPortal(returnUrl?: string): Promise<void> {
  const { supabase } = await import("@/lib/supabase");
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) throw new Error("Not authenticated");

  const { data, error } = await supabase.functions.invoke<{ url: string }>(
    "create-portal-session",
    {
      body:    { returnUrl: returnUrl ?? window.location.href },
      headers: { Authorization: `Bearer ${session.session.access_token}` },
    }
  );

  if (error || !data?.url) {
    throw new Error(error?.message ?? "Could not open billing portal");
  }

  window.location.href = data.url;
}

// ─── Tier helpers ─────────────────────────────────────────────
export type ProposalTier = "starter" | "growth" | "pro" | "none";

export const TIER_PROPOSAL_LIMITS: Record<ProposalTier, number | null> = {
  starter: 10,
  growth:  50,
  pro:     null, // unlimited
  none:    0,
};

export const TIER_LABELS: Record<ProposalTier, string> = {
  starter: "Starter — $49/mo",
  growth:  "Growth — $99/mo",
  pro:     "Pro — $199/mo",
  none:    "No plan",
};

/**
 * Derive the active proposal tier from a subscription's Stripe price IDs.
 * Price IDs come from the subscriptions table → Stripe product mapping.
 */
export function getTierFromPriceId(priceId: string | null): ProposalTier {
  if (!priceId) return "none";

  const STARTER_ID = import.meta.env.VITE_STRIPE_PROPOSAL_STARTER_PRICE_ID as string;
  const GROWTH_ID  = import.meta.env.VITE_STRIPE_PROPOSAL_GROWTH_PRICE_ID  as string;
  const PRO_ID     = import.meta.env.VITE_STRIPE_PROPOSAL_PRO_PRICE_ID     as string;

  if (priceId === STARTER_ID) return "starter";
  if (priceId === GROWTH_ID)  return "growth";
  if (priceId === PRO_ID)     return "pro";
  return "none";
}
